import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

// Create axios instance for admin
const adminApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'X-App-Context': 'admin', // Add admin context header
  },
});

// Request interceptor to add auth token
adminApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('adminAuthToken');
    // console.log('🔍 Admin API Request Debug:');
    // console.log('  - URL:', config.url);
    // console.log('  - Method:', config.method?.toUpperCase());
    // console.log('  - Token exists in localStorage:', !!token);
    // console.log('  - Token (first 20 chars):', token ? token.substring(0, 20) + '...' : 'No token');
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      // console.log('  - Authorization header set');
    } else {
      // console.log('  - No token found, skipping Authorization header');
    }
    return config;
  },
  (error) => {
    console.error('❌ Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
adminApi.interceptors.response.use(
  (response) => response,
  (error) => {
    // Enhanced error handling for production
    const status = error.response?.status;
    const errMsg = (error.response?.data?.error || error.response?.data?.message || '').toString();
    const isExpiredToken = status === 403 && (
      errMsg.toLowerCase().includes('invalid or expired token') ||
      errMsg.toLowerCase().includes('token has expired')
    );

    if (status === 401 || isExpiredToken) {
      // Don't redirect if we're making a login request or already on login page
      const isLoginRequest = error.config?.url?.includes('/auth/login');
      const isOnLoginPage = window.location.pathname === '/login';
      
      if (!isLoginRequest && !isOnLoginPage) {
        localStorage.removeItem('adminAuthToken');
        localStorage.removeItem('adminUser');
        window.location.href = '/login';
      }
      // If it's a login request or we're on login page, just let the error pass through
    }
    
    // Log detailed error information in production
    if (process.env.NODE_ENV === 'production') {
      console.error('Admin API Error:', {
        url: error.config?.url,
        method: error.config?.method,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
        timestamp: new Date().toISOString()
      });
    }
    
    // Ensure error has proper structure for consistent handling
    if (!error.response) {
      error.response = {
        status: 500,
        statusText: 'Network Error',
        data: { error: 'Network connection failed. Please check your internet connection and try again.' }
      };
    }
    
    return Promise.reject(error);
  }
);

// Admin Auth API
export const adminAuthAPI = {
  login: (credentials: { email: string; password: string; requestedRole?: string; recaptchaToken?: string }) =>
    adminApi.post('/auth/login', credentials),
};

// Admin Management API
export const adminManagementAPI = {
  getDashboard: () => adminApi.get('/admin/dashboard'),
  getUsers: (params: any) => adminApi.get('/admin/users', { params }),
  getCandidates: (params: any) => adminApi.get('/admin/candidates', { params }),
  getCandidateById: (id: string) => adminApi.get(`/admin/candidates/${id}`),
  getEmployers: (params: any) => adminApi.get('/admin/employers', { params }),
  getEmployerById: (id: string) => adminApi.get(`/admin/employers/${id}`),
  toggleUserStatus: (userId: string) => adminApi.patch(`/admin/users/${userId}/toggle-status`),
  toggleUserRoleStatus: (userId: string, role: string) => adminApi.patch(`/admin/users/${userId}/toggle-status`, { role }),
  createUser: (userData: any) => adminApi.post('/admin/users', userData),
  updateUserRoles: (userId: string, data: { roles: string[], action?: 'add' | 'remove' | 'set' }) => 
    adminApi.patch(`/admin/users/${userId}/roles`, data),
  
  // Admin Profile APIs
  getProfile: () => adminApi.get('/admin/profile'),
  updateProfile: (profileData: { firstName: string; lastName: string }) => 
    adminApi.put('/admin/profile', profileData),
  changePassword: (passwordData: { currentPassword: string; newPassword: string }) => 
    adminApi.post('/auth/change-password', passwordData),
  
  // User Activity API - Comprehensive activity history
  getUserActivity: (userId: string, params?: { limit?: number }) => 
    adminApi.get(`/admin/users/${userId}/activity`, { params }),
  
  // Candidate Profile Summary APIs
  getCandidateProfileSummary: (profileId: string, params?: any) => 
    adminApi.get(`/candidates/${profileId}/profile-summary`, { params }),
  getCandidateQuickOverview: (profileId: string) => 
    adminApi.get(`/candidates/${profileId}/quick-overview`),
  getDashboardSummary: (params?: any) => 
    adminApi.get('/candidates/dashboard-summary', { params }),
  getBulkProfileSummaries: (candidateIds: string[], options?: any) => 
    adminApi.post('/candidates/bulk-summary', { candidateIds, options }),
  
  // Skill Analytics APIs
  getMarketabilityInsights: (params?: any) => 
    adminApi.get('/candidates/marketability-insights', { params }),
  getSkillAnalytics: (params?: any) => 
    adminApi.get('/candidates/skill-analytics', { params }),
  assessRoleAlignment: (data: {
    jobTitle: string;
    requiredSkills: string[];
    preferredSkills?: string[];
    minExperience?: number;
    candidateIds?: string[];
  }) => adminApi.post('/candidates/role-alignment', data),

  // Tech Monitoring APIs
  getApiLogs: (filters?: any) => adminApi.get('/admin/logs/api', { params: filters }),
  getDatabaseLogs: (filters?: any) => adminApi.get('/admin/logs/database', { params: filters }),
  getAuthLogs: (filters?: any) => adminApi.get('/admin/logs/auth', { params: filters }),
  getResumeViewLogs: (filters?: any) => adminApi.get('/admin/logs/resume-views', { params: filters }),
  getLogsStats: () => adminApi.get('/admin/logs/stats'),
  
  // API Metrics (we'll add this endpoint to backend)
  getApiMetrics: (params?: any) => adminApi.get('/admin/metrics/api', { params }),
  getEndpointMetrics: (endpoint?: string) => adminApi.get('/admin/metrics/endpoints', { params: { endpoint } }),
  getSystemHealth: () => adminApi.get('/admin/system/health'),
  getRealTimeMetrics: () => adminApi.get('/admin/metrics/realtime'),
};

// System API
export const systemAPI = {
  getSystemStats: () => adminApi.get('/admin/system/stats'),
  backupData: () => adminApi.post('/admin/system/backup'),
};

export default adminApi;